import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import Comment from '../components/Comment';
import Prism from 'prismjs';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';
import './PostDetail.css';

function PostDetail({ user, onCommentCountChange, deletePost }) {
    const { id } = useParams();
    const { hash } = useLocation();
    const navigate = useNavigate();

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [newCommentText, setNewCommentText] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [copied, setCopied] = useState(false);

    const [votes, setVotes] = useState(0);
    const [userVoteStatus, setUserVoteStatus] = useState(0);

    const fetchPostDetail = useCallback(async () => {
        try {
            setLoading(true);
            const [postRes, commentsRes] = await Promise.all([
                API.get(`/posts/${id}`),
                API.get(`/posts/${id}/comments`)
            ]);

            const fetchedPost = postRes.data;
            setPost(fetchedPost);
            setComments(commentsRes.data || []);

            const netVotes = (fetchedPost.upvote_count !== undefined || fetchedPost.downvote_count !== undefined)
                ? ((fetchedPost.upvote_count || 0) - (fetchedPost.downvote_count || 0))
                : (fetchedPost.votes ?? 0);
            setVotes(netVotes);
            setUserVoteStatus(fetchedPost.user_vote || 0);
        } catch (err) {
            setError(err.message || "Gönderi yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchPostDetail();
    }, [fetchPostDetail]);

    useEffect(() => {
        if (hash && !loading) {
            const targetId = hash.replace('#', '');
            const element = document.getElementById(targetId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.classList.add('highlight-comment');
                    setTimeout(() => element.classList.remove('highlight-comment'), 2000);
                }, 100);
            }
        }
    }, [hash, loading]);

    const handleVote = async (type) => {
        if (!user) {
            alert("Oy kullanmak için giriş yapmalısın.");
            return;
        }
        try {
            const response = await API.post('/votes', {
                target_type: 'post',
                target_id: id,
                vote_value: type
            });
            const removed = response.data?.removed;
            const newVoteType = removed ? 0 : type;
            const diff = newVoteType - userVoteStatus;
            setVotes(prev => prev + diff);
            setUserVoteStatus(newVoteType);
        } catch (err) {
            alert("Oy kaydedilirken hata oluştu: " + err.message);
        }
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newCommentText.trim()) return;
        if (!user) {
            alert("Yorum yapmak için giriş yapmalısın.");
            return;
        }
        try {
            const response = await API.post(`/posts/${id}/comments`, {
                body: newCommentText.trim(),
                parent_id: null
            });
            const newComment = {
                ...response.data,
                author_username: user.username,
                author_avatar: user.avatar,
                replies: []
            };
            setComments(prev => [...prev, newComment]);
            setNewCommentText("");
            const newCount = (post.comment_count || 0) + 1;
            setPost(prev => prev ? { ...prev, comment_count: newCount } : prev);
            if (onCommentCountChange) onCommentCountChange(id, { comment_count: newCount });
        } catch (err) {
            alert("Yorum gönderilirken hata oluştu: " + err.message);
        }
    };

    const handleReplySubmit = async (targetCommentId, replyText) => {
        if (!replyText.trim()) return;
        if (!user) {
            alert("Yanıt yazmak için giriş yapmalısın.");
            return;
        }
        try {
            const response = await API.post(`/posts/${id}/comments`, {
                body: replyText.trim(),
                parent_id: targetCommentId
            });
            const newReplyObj = {
                ...response.data,
                author_username: user.username,
                author_avatar: user.avatar,
                replies: []
            };
            const addReplyRecursive = (commentList) => commentList.map(comment => {
                if (comment.id === targetCommentId) {
                    return { ...comment, replies: [...(comment.replies || []), newReplyObj] };
                }
                if (comment.replies && comment.replies.length > 0) {
                    return { ...comment, replies: addReplyRecursive(comment.replies) };
                }
                return comment;
            });
            setComments(prev => addReplyRecursive(prev));
            const newCount = (post.comment_count || 0) + 1;
            setPost(prev => prev ? { ...prev, comment_count: newCount } : prev);
            if (onCommentCountChange) onCommentCountChange(id, { comment_count: newCount });
        } catch (err) {
            alert("Yanıt gönderilirken hata oluştu: " + err.message);
        }
    };

    const handleAcceptAnswer = async (commentId) => {
        try {
            await API.post(`/posts/${id}/accept-answer`, { commentId });
            setPost(prev => prev ? { ...prev, accepted_comment_id: commentId, is_resolved: true } : prev);
        } catch (err) {
            alert("Cevap kabul edilirken hata oluştu: " + err.message);
        }
    };

    const handleUnacceptAnswer = async () => {
        try {
            await API.delete(`/posts/${id}/accept-answer`);
            setPost(prev => prev ? { ...prev, accepted_comment_id: null, is_resolved: false } : prev);
        } catch (err) {
            alert("Kabul kaldırılırken hata oluştu: " + err.message);
        }
    };

    const handleDeletePost = async () => {
        if (!window.confirm("Bu gönderiyi silmek istediğine emin misin? Bu işlem geri alınamaz.")) return;
        try {
            setIsDeleting(true);
            if (deletePost) {
                await deletePost(id);
            } else {
                await API.delete(`/posts/${id}`);
            }
            navigate('/');
        } catch (err) {
            alert("Gönderi silinirken hata oluştu: " + err.message);
            setIsDeleting(false);
        }
    };

    if (loading) return <div className="post-detail-container">Yükleniyor...</div>;
    if (error || !post) return <div className="post-detail-container">{error || "Gönderi bulunamadı."}</div>;

    const authorName = post.author?.username
        || post.profiles?.username
        || (typeof post.author === 'string' ? post.author : null)
        || "Anonim";
    const authorAvatar = post.author?.avatar_url || post.authorAvatar || null;
    const viewsCount = post.view_count ?? post.views ?? 0;
    const descriptionText = post.body || post.description || post.content || '';
    const isOwner = Boolean(user && post.author_id && user.id === post.author_id);

    // Kod sorusu mu? (CodeSoruSor.js böyle etiketliyor) — öyleyse açıklamayı
    // syntax-highlight'lı bir kod bloğu olarak gösteriyoruz.
    const tags = Array.isArray(post.tags) ? post.tags : [];
    const isCodeQuestion = tags.includes('code-question');
    const codeLanguage = tags.find(t => t !== 'code-question' && Prism.languages[t]) || 'javascript';

    const handleCopyCode = () => {
        navigator.clipboard.writeText(descriptionText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="post-detail-container">
            <div className="post-full-content">
                <div className="post-vote-side" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '20px' }}>
                    <button
                        className={`vote-btn ${userVoteStatus === 1 ? 'voted' : ''}`}
                        onClick={() => handleVote(1)}
                        style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: userVoteStatus === 1 ? 'var(--accent)' : 'var(--line-strong)' }}
                        aria-label="Upvote"
                    >
                        ▲
                    </button>
                    <span className="vote-count" style={{ fontWeight: 'bold', fontSize: '16px' }}>{votes}</span>
                </div>

                <div className="post-main-side" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <h1 style={{ margin: 0 }}>
                            {post.title}
                            {post.is_resolved && (
                                <span style={{ marginLeft: '10px', fontSize: '13px', color: 'var(--ok)', border: '1px solid var(--ok)', borderRadius: '4px', padding: '2px 8px', verticalAlign: 'middle' }}>
                                    ✓ Çözüldü
                                </span>
                            )}
                            {isCodeQuestion && (
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: 'var(--accent-ink)', background: 'var(--accent-soft)', borderRadius: '4px', padding: '2px 8px', verticalAlign: 'middle', fontWeight: 600 }}>
                                    {'</>'} {codeLanguage.toUpperCase()}
                                </span>
                            )}
                        </h1>

                        {isOwner && (
                            <button
                                onClick={handleDeletePost}
                                disabled={isDeleting}
                                style={{
                                    background: 'none', border: '1px solid var(--danger)', color: 'var(--danger)',
                                    borderRadius: '4px', padding: '6px 12px', fontSize: '13px',
                                    cursor: isDeleting ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'
                                }}
                            >
                                {isDeleting ? 'Siliniyor...' : '🗑 Gönderiyi Sil'}
                            </button>
                        )}
                    </div>

                    {/* Yazar bilgisi — avatarlı */}
                    <div className="post-meta" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                        <img
                            src={authorAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(authorName)}&background=262569&color=fff&size=28`}
                            alt={authorName}
                            style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <span>Yazan: <strong>@{authorName}</strong></span> • <span>{viewsCount} izlenme</span>
                    </div>
                    <hr />

                    {/* İçerik: kod sorusuysa syntax-highlight'lı blok, değilse düz metin */}
                                        {isCodeQuestion ? (
                        <div style={{
                            marginTop: '16px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 18px rgba(0,0,0,0.12)'
                        }}>
                            {/* VS Code benzeri başlık çubuğu */}
                            <div style={{
                                background: '#1e1e1e',
                                padding: '10px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid var(--ink)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#ff5f56', display: 'inline-block' }}></span>
                                    <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#ffbd2e', display: 'inline-block' }}></span>
                                    <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#27c93f', display: 'inline-block' }}></span>
                                    <span style={{ color: 'var(--muted)', fontSize: '12.5px', fontFamily: 'monospace', marginLeft: '10px' }}>
                                        çözüm.{codeLanguage === 'python' ? 'py' : codeLanguage === 'cpp' ? 'cpp' : codeLanguage === 'java' ? 'java' : 'js'}
                                    </span>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    style={{
                                        background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
                                        borderRadius: '6px', padding: '5px 12px', fontSize: '12.5px', cursor: 'pointer', fontWeight: 600
                                    }}
                                >
                                    {copied ? '✓ Kopyalandı' : '📋 Kopyala'}
                                </button>
                            </div>

                            {/* Kod gövdesi — büyük, ferah, gerçek editör hissi */}
                            <pre style={{
                                background: '#1e1e1e', color: '#f8f8f2', padding: '28px 24px', margin: 0,
                                overflowX: 'auto', fontSize: '16px', lineHeight: '1.7',
                                fontFamily: '"Fira Code", "Fira Mono", Consolas, monospace'
                            }}>
                                <code
                                    dangerouslySetInnerHTML={{
                                        __html: Prism.highlight(
                                            descriptionText,
                                            Prism.languages[codeLanguage] || Prism.languages.javascript,
                                            codeLanguage
                                        )
                                    }}
                                />
                            </pre>
                        </div>
                    ) : (
                        <div className="post-text">
                            {descriptionText}
                        </div>
                    )}

                    <div className="post-tags" style={{ marginTop: '14px' }}>
                        {tags.filter(t => t !== 'code-question').map((tag, index) => (
                            <span key={index} className="tag-item">{tag}</span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="comments-section">
                <h3>{comments.length} Yorum</h3>

                <form className="comment-input-area" onSubmit={handleCommentSubmit}>
                    <textarea
                        placeholder={user ? "Düşüncelerini paylaş..." : "Yorum yapmak için giriş yapmalısın"}
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        disabled={!user}
                    ></textarea>
                    <button type="submit" className="comment-btn-submit" disabled={!user}>Yorum Yap</button>
                </form>

                <div className="comments-list">
                    {comments.map(comment => (
                        <Comment
                            key={comment.id}
                            comment={comment}
                            onReplySubmit={handleReplySubmit}
                            postOwnerId={post.author_id}
                            currentUserId={user?.id}
                            acceptedCommentId={post.accepted_comment_id}
                            onAccept={handleAcceptAnswer}
                            onUnaccept={handleUnacceptAnswer}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default PostDetail;